import React from 'react';

import type { ReferralInfo } from '../services/referralService';

import './ReferralProgram.css';

/** What a referred friend earns the referrer (referral_service: SIGNUP_CREDIT_REWARD, SUBSCRIPTION_CREDIT_REWARD). */
const SIGNUP_CREDITS = 5;
const SUBSCRIBE_CREDITS = 20;

export const CREDITS_PER_FRIEND = SIGNUP_CREDITS + SUBSCRIBE_CREDITS;

/** A reel earns 1 credit per 10 views, at most 100, and one reel can be posted each day (promotion_service). */
const REEL_CREDITS_PER_DAY = 100;

interface ReferralProgramProps {
  error: string | null;
  info: ReferralInfo | null;
  /** The ways to earn, one row each (Invite contacts first, then your link and a reel). */
  children?: React.ReactNode;
}

const RewardPath: React.FC = () => (
  <ol aria-label="What you earn" className="refer-path">
    <li>
      <span aria-hidden="true" className="refer-path__step">1</span>
      <span className="refer-path__copy"><strong>+{SIGNUP_CREDITS} credits</strong> when a friend signs up</span>
    </li>
    <li aria-hidden="true" className="refer-path__line" />
    <li>
      <span aria-hidden="true" className="refer-path__step">2</span>
      <span className="refer-path__copy"><strong>+{SUBSCRIBE_CREDITS} credits</strong> when they subscribe</span>
    </li>
  </ol>
);

const ReferralHero: React.FC<{ info: ReferralInfo | null }> = ({ info }) => (
  <section aria-labelledby="refer-hero-title" className="refer-hero">
    <div className="refer-hero__top">
      <div className="refer-hero__copy">
        <span className="refer-eyebrow">Your rewards</span>
        <h3 id="refer-hero-title">Get credits for each friend.</h3>
      </div>
      <dl aria-label="Referral summary" className="refer-stats">
        <div><dt>Friends referred</dt><dd>{info?.total_referred ?? '–'}</dd></div>
        <div><dt>Credits earned</dt><dd className="refer-stats__amber">{info?.credits_earned ?? '–'}</dd></div>
      </dl>
    </div>
    <RewardPath />
    <p className="refer-hero__limit">
      {info?.referrals_remaining === 0
        ? `Your link reached its ${info.referral_limit}-friend limit.`
        : `Your link: up to ${info?.referral_limit ?? 10} friends.`}
    </p>
  </section>
);

/** Before a way's button: the most it can still give in total (amber), and what each friend (or view) gives. */
export const WayReward: React.FC<{ total: string; each: string; title: string }> = ({ total, each, title }) => (
  <span className="refer-way__reward" title={title}>
    <strong>{total}</strong>
    <span>{each}</span>
  </span>
);

const LinkIcon: React.FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
    <path d="M8.5 11.5a3.2 3.2 0 0 0 4.6.1l2.6-2.6a3.2 3.2 0 0 0-4.5-4.5l-1.1 1" />
    <path d="M11.5 8.5a3.2 3.2 0 0 0-4.6-.1L4.3 11a3.2 3.2 0 0 0 4.5 4.5l1.1-1" />
  </svg>
);

/** The link without its scheme, short enough to sit beside the row's title. */
const shortLink = (link?: string): string => (link ? link.replace(/^https?:\/\//, '') : 'Loading…');

interface ReferralLinkRowProps {
  copied: boolean;
  info: ReferralInfo | null;
  onCopy: () => void;
}

/** Share your link: the personal link beside the title, what is left to earn from it, and Copy link. */
export const ReferralLinkRow: React.FC<ReferralLinkRowProps> = ({ copied, info, onCopy }) => (
  <section aria-labelledby="refer-link-title" className="refer-way">
    <span className="refer-way__icon"><LinkIcon /></span>
    <div className="refer-way__copy">
      <div className="refer-way__title">
        <h4 id="refer-link-title">Share your link</h4>
        <span className="refer-link__url">{shortLink(info?.referral_link)}</span>
      </div>
      <p>Earn credits for each friend who signs up.</p>
    </div>
    <div className="refer-way__side">
      <WayReward each={`${CREDITS_PER_FRIEND} cr / friend`}
        title={`${CREDITS_PER_FRIEND} credits for each friend, up to ${info?.referral_limit ?? 10} friends.`}
        total={info ? `${info.referrals_remaining * CREDITS_PER_FRIEND} cr max` : '–'} />
      <button
        className={`refer-way__action refer-link__copy${copied ? ' is-copied' : ''}`}
        disabled={!info?.referral_link || info.referrals_remaining === 0}
        onClick={onCopy}
        type="button"
      >
        {info?.referrals_remaining === 0 ? 'Limit reached' : copied ? '✓ Copied' : 'Copy link'}
      </button>
    </div>
  </section>
);

const ReelIcon: React.FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
    <rect height="12" rx="3" width="15" x="2.5" y="4" />
    <path d="m8.4 7.4 4.4 2.6-4.4 2.6V7.4Z" />
  </svg>
);

/** The way that is not an invite: a reel of one of your projects, paid by its views. */
export const ShareReelRow: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <section aria-labelledby="refer-reel-title" className="refer-way">
    <span className="refer-way__icon"><ReelIcon /></span>
    <div className="refer-way__copy">
      <div className="refer-way__title"><h4 id="refer-reel-title">Share a reel</h4></div>
      <p>Post a reel of your project. Earn credits from its views.</p>
    </div>
    <div className="refer-way__side">
      <WayReward each="1 cr / 10 views" title="1 credit for each 10 views, up to 100 for a reel. One reel each day."
        total={`${REEL_CREDITS_PER_DAY} cr / day max`} />
      <button className="refer-way__action refer-way__button" onClick={onOpen} type="button">Make a reel</button>
    </div>
  </section>
);

/**
 * Refer & earn: every way to earn as a row (``children``: Invite contacts, Share your link, Share a reel), then what
 * you earn for each friend. (Picking people, for a project or for 8080.AI, is its own window: Invite people.)
 */
export const ReferralProgram: React.FC<ReferralProgramProps> = ({ error, info, children }) => (
  <div className="refer-program">
    {error && <p className="refer-error" role="alert">{error}</p>}
    <h3 className="refer-section-label">Ways to earn credits</h3>
    <div className="refer-ways">{children}</div>
    <ReferralHero info={info} />
  </div>
);
